/* eslint-disable @typescript-eslint/no-empty-interface */
import React from 'react';
import { AllieLoading } from '@allie/app-ui-lib';
import { loadScript } from './helpers';

export interface BaseMFEProps extends CommonAttrs {
  exposedPath: string;
  loadingMessage?: string;
  loadingErrorMessage?: string;
  name: string;
  entryPointUrl: string;
  className?: string;
}

export type BaseMFEMountParams = CommonAttrs

export interface BaseMFEMountResponse {
  onParamsChange: any;
}

interface CommonAttrs {}

type MountType<T, R> = (el: Element, params: T) => R;
type UnmountType = (ref: unknown) => void;

interface BaseMFEApp<T, R> {
  mount: MountType<T, R>;
  unmount?: UnmountType;
}

type MyState = {
  isLoading: boolean;
  isError: boolean;
  unmount?: UnmountType;
  update?: any;
};

export abstract class BaseMicroFrontend<
  T extends BaseMFEProps = any,
> extends React.Component {
  declare props: T;

  componentRef: React.RefObject<unknown>;
  state: MyState = {
    isLoading: true,
    isError: false,
  };
  constructor(props: T) {
    super(props);
    this.props = props;
    this.componentRef = React.createRef();
  }

  abstract buildMountParams(): BaseMFEMountParams;
  abstract afterMount(response: BaseMFEMountResponse): void;

  afterLoad(app: () => BaseMFEApp<BaseMFEMountParams, BaseMFEMountResponse>) {
    const params = this.buildMountParams();

    const { mount, unmount } = app();
    this.setState({ mount, unmount, isLoading: false });

    const response = mount(this.componentRef.current as Element, params);

    this.setState({ update: response.onParamsChange });
  }

  shouldComponentUpdate(
    nextProps: any,
  ): boolean {
    this.state.update?.(nextProps);

    return true;
  }

  async componentDidMount() {
    const { exposedPath, name, entryPointUrl } = this.props;

    try {
      await loadScript(name, entryPointUrl);
      const app = await (window[name as any] as any).get(exposedPath);

      this.afterLoad(app);

      console.log(`${name}-${exposedPath}: componentDidMount`);
    } catch (error) {
      console.log(`${name}-${exposedPath}: Error ${error}`);
      this.setState({ isLoading: false, isError: true });
    }
  }

  componentWillUnmount() {
    const { unmount } = this.state;
    const el = this.componentRef.current;
    if (unmount && el) unmount(el);
  }

  render() {
    const { isLoading, isError } = this.state;
    const { className, loadingMessage, loadingErrorMessage } = this.props;
    return (
      <>
        {/* Duration added as workaround for double mounting, when that is fixed, remove it */}
        {loadingMessage && <AllieLoading
          isOpen={isLoading}
          message={loadingMessage}
          duration={5000}
        />}
        {isError && (loadingErrorMessage || 'There was a problem loading this section')}
        <div className={className} ref={node => ((this.componentRef as any).current = node)} />
      </>
    );
  }
}
